// Schematic layout for the whole-school minimap. The minimap IS the campus overworld (32×37)
// scaled down: agents in the cour plot at their real campus tile; agents inside a building plot
// inside that building's FOOTPRINT rectangle (their interior tile scaled into the rect). Derived
// from the campus ASCII so it always matches the real map.

import { LOCATIONS } from './greybox/campusMaps'
import { SceneName } from './enums/SceneNames'
import type { LocationId, Tile } from '../net/protocol'

const campusCfg = LOCATIONS.find(c => c.id === SceneName.Campus)!
const CAMPUS_ASCII = campusCfg.ascii
export const CAMPUS_W = Math.max(...CAMPUS_ASCII.map(r => r.length))
export const CAMPUS_H = CAMPUS_ASCII.length

function cellColor(ch: string): number {
    switch (ch) {
        case '#': return 0x5b6472 // bâtiment
        case ',': return 0xcdbb95 // cour (béton)
        case 'C': return 0x8fbf6b // terrain de sport
        case 'D': return 0xb9772f // porte
        case 'H': return 0x2f5d2f // haie
        case 'T': return 0x4f7a3a // arbre
        default: return 0x6aa84f  // herbe / sol
    }
}

/** [row][col] → fill colour, for the static schematic background. */
export const CAMPUS_CELLS: number[][] = CAMPUS_ASCII.map(row => {
    const cells: number[] = []
    for (let x = 0; x < CAMPUS_W; x++) cells.push(cellColor(row[x] ?? '.'))
    return cells
})

type Rect = { x: number; y: number; w: number; h: number }
// Building footprints on the campus grid (interiors are their own scenes → plot agents inside the rect).
const FOOTPRINTS: Partial<Record<LocationId, Rect>> = {
    // Bâtiment Secondaire (E, cols 20–28 / rows 14–19) split into 3 salle strips by door (A/B/C).
    classroom: { x: 20, y: 14, w: 3, h: 6 },
    'classroom-b': { x: 23, y: 14, w: 3, h: 6 },
    'classroom-c': { x: 26, y: 14, w: 3, h: 6 },
    cantine: { x: 2, y: 28, w: 6, h: 2 },    // SW
    gymnase: { x: 9, y: 28, w: 8, h: 2 },    // S
    // Bâtiment Principal (NW, cols 2–16 / rows 4–10) split into strips by door.
    administration: { x: 2, y: 4, w: 3, h: 7 },
    'vie-scolaire': { x: 5, y: 4, w: 3, h: 7 },
    cdi: { x: 8, y: 4, w: 3, h: 7 },
    infirmerie: { x: 11, y: 4, w: 3, h: 7 },
    'salle-profs': { x: 14, y: 4, w: 3, h: 7 },
}

// Interior grid dims (to scale an interior tile into its footprint), from each room's ASCII.
const DIMS: Record<string, { w: number; h: number }> = {}
for (const c of LOCATIONS) DIMS[c.id] = { w: Math.max(...c.ascii.map(r => r.length)), h: c.ascii.length }

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v))

/** Map (location, interior tile) → a cell (col,row) on the campus-grid schematic. */
export function toMinimapCell(location: LocationId, tile: Tile): { col: number; row: number } {
    const f = FOOTPRINTS[location]
    if (!f) return { col: clamp(tile.x, 0, CAMPUS_W - 1), row: clamp(tile.y, 0, CAMPUS_H - 1) } // campus → real tile
    const d = DIMS[location] ?? { w: 16, h: 12 }
    return {
        col: f.x + Math.min(f.w - 1, Math.floor((tile.x / Math.max(1, d.w)) * f.w)),
        row: f.y + Math.min(f.h - 1, Math.floor((tile.y / Math.max(1, d.h)) * f.h)),
    }
}
